<template>
  <v-main>
    <v-container fluid class="supervisor-org-dashboard">
      <div class="text-xl font-bold mb-6">{{ t("supervisorOrgDashboard.title") }}</div>

      <template v-if="loading">
        <v-row class="mb-4">
          <v-col v-for="i in 3" :key="i" cols="12" md="4">
            <v-card border flat rounded="lg">
              <v-card-text class="p-6">
                <v-skeleton-loader type="text" width="55%" />
                <v-skeleton-loader type="heading" class="mt-3" width="75%" />
                <v-skeleton-loader type="text" width="35%" class="mt-3" />
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <v-card border flat rounded="lg">
          <v-card-text class="p-0">
            <v-row class="items-stretch" no-gutters>
              <v-col
                v-for="i in 5"
                :key="i"
                cols="12"
                sm="6"
                lg
                class="supervisor-org-dashboard__secondary-col"
              >
                <div class="supervisor-org-dashboard__metric-cell p-6">
                  <v-skeleton-loader type="text" width="55%" />
                  <v-skeleton-loader type="heading" class="mt-2" width="40%" />
                </div>
                <v-divider
                  v-if="i < 5"
                  class="supervisor-org-dashboard__divider hidden lg:flex"
                  color="#616161"
                  vertical
                />
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <section class="mt-8">
          <div class="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div class="text-xl font-bold">
              {{ t("supervisorOrgDashboard.approvalQueue.title") }}
            </div>
            <v-skeleton-loader type="text" width="140" />
          </div>
          <v-row>
            <v-col v-for="i in 3" :key="i" cols="12" sm="6" lg="4">
              <v-card border flat rounded="lg" class="supervisor-org-dashboard__approval-card">
                <v-card-text class="p-5">
                  <v-skeleton-loader type="text" width="45%" />
                  <v-skeleton-loader type="heading" width="60%" class="mt-3" />
                  <v-skeleton-loader type="text" width="55%" class="mt-2" />
                  <v-skeleton-loader type="text" width="40%" class="mt-2" />
                  <v-skeleton-loader type="button" class="mt-4" />
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </section>
      </template>

      <template v-else-if="dashboardData">
        <v-row class="mb-4">
          <v-col v-for="card in primaryCards" :key="card.key" cols="12" md="4">
            <v-card class="supervisor-org-dashboard__primary-card" border flat rounded="lg">
              <v-card-text class="p-6">
                <div class="supervisor-org-dashboard__label">{{ card.label }}</div>
                <div class="supervisor-org-dashboard__value mt-2">{{ card.value }}</div>
                <div class="supervisor-org-dashboard__subtitle mt-2">{{ card.subtitle }}</div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <v-card class="supervisor-org-dashboard__secondary-card border-theme" flat rounded="lg">
          <v-card-text class="p-0">
            <v-row class="supervisor-org-dashboard__secondary-row items-stretch" no-gutters>
              <v-col
                v-for="(metric, index) in secondaryMetrics"
                :key="metric.key"
                cols="12"
                sm="6"
                lg
                class="supervisor-org-dashboard__secondary-col"
              >
                <div class="supervisor-org-dashboard__metric-cell p-6">
                  <div class="supervisor-org-dashboard__label">{{ metric.label }}</div>
                  <div
                    class="supervisor-org-dashboard__secondary-value mt-2"
                    :class="metric.valueClass ?? 'text-high-emphasis'"
                  >
                    {{ metric.value }}
                  </div>
                </div>
                <v-divider
                  v-if="index < secondaryMetrics.length - 1"
                  class="supervisor-org-dashboard__divider hidden lg:flex"
                  color="#616161"
                  vertical
                />
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <section class="mt-8">
          <div class="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div class="text-xl font-bold">
              {{ t("supervisorOrgDashboard.approvalQueue.title") }}
            </div>
            <div v-if="approvalQueueSummary" class="supervisor-org-dashboard__subtitle">
              {{ approvalQueueSummary }}
            </div>
          </div>

          <div
            v-if="pendingTransferRequests.length === 0"
            class="supervisor-org-dashboard__subtitle text-medium-emphasis"
          >
            {{ t("supervisorOrgDashboard.approvalQueue.empty") }}
          </div>

          <v-row v-else>
            <v-col
              v-for="card in pendingTransferRequestCards"
              :key="card.key"
              cols="12"
              sm="6"
              lg="4"
            >
              <v-card class="supervisor-org-dashboard__approval-card" border flat rounded="lg">
                <v-card-text class="p-5 flex flex-col h-full">
                  <RouterLink
                    :to="card.transfersRoute"
                    class="supervisor-org-dashboard__label supervisor-org-dashboard__account-name supervisor-org-dashboard__account-link"
                  >
                    {{ card.accountName }}
                  </RouterLink>
                  <div class="supervisor-org-dashboard__approval-amount mt-3">
                    {{ card.formattedAmount }}
                  </div>
                  <div class="text-base font-medium mt-2">
                    {{ card.beneficiaryName }}
                  </div>
                  <div class="supervisor-org-dashboard__subtitle mt-2">
                    {{ card.formattedTime }}
                  </div>
                  <v-btn
                    class="mt-4"
                    color="primary"
                    block
                    @click="openPendingTransferRequest(card.accountId, card.id)"
                  >
                    {{ t("supervisorOrgDashboard.examine") }}
                  </v-btn>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </section>
      </template>

      <div v-else class="text-medium-emphasis">{{ t("common.noData") }}</div>
    </v-container>

    <PendingTransferRequestDetailDrawer
      v-if="drawerAccountId"
      :key="drawerAccountId"
      :account-id="drawerAccountId"
      temporary
    />
  </v-main>
</template>

<script lang="ts" setup>
import { useI18n } from "@/composables/useI18n";
import { call } from "@/firebase";
import { useSelectedTransferRequestStore } from "@/store/selectedTransferRequest";
import { useToast } from "@/store/toast";
import { useUserStore } from "@/store/user";
import { formatCurrency } from "@/utils/tools";
import PendingTransferRequestDetailDrawer from "@/views/Account/components/PendingTransferRequestDetailDrawer.vue";
import type { CallablesMap } from "@aircap/common/callables";
import { captureException } from "@sentry/vue";
import { storeToRefs } from "pinia";
import { computed, onMounted, ref, watch } from "vue";
import type { RouteLocationRaw } from "vue-router";

type SupervisorOrgDashboardData = CallablesMap["getSupervisorOrgDashboard"]["response"];
type PendingTransferRequest = SupervisorOrgDashboardData["pendingTransferRequests"][number];

type PendingTransferRequestCard = PendingTransferRequest & {
  key: string;
  formattedAmount: string;
  formattedTime: string;
  transfersRoute: RouteLocationRaw;
};

const { t, locale } = useI18n();
const user = useUserStore();
const { showErrorToast } = useToast();
const { openTransferRequestDrawer } = useSelectedTransferRequestStore();
const { selectedTransferRequestId } = storeToRefs(useSelectedTransferRequestStore());

const loading = ref(true);
const dashboardData = ref<SupervisorOrgDashboardData | null>(null);
const drawerAccountId = ref<string | null>(null);

const formatEuro = (value: number) =>
  formatCurrency({ value, currencyCode: "EUR", locale: locale.value });

const pendingTransferRequests = computed(() => dashboardData.value?.pendingTransferRequests ?? []);

const approvalQueueSummary = computed(() => {
  const count = pendingTransferRequests.value.length;
  if (count === 0) return "";
  return t("supervisorOrgDashboard.approvalQueue.summary", { count });
});

const pendingTransferRequestCards = computed((): PendingTransferRequestCard[] => {
  const dateFormatter = new Intl.DateTimeFormat(locale.value, {
    dateStyle: "medium",
    timeStyle: "short",
  });

  return pendingTransferRequests.value.map((transferRequest) => ({
    ...transferRequest,
    key: `${transferRequest.accountId}-${transferRequest.id}`,
    formattedAmount: formatCurrency({
      value: parseFloat(transferRequest.amount),
      currencyCode: transferRequest.currency,
      locale: locale.value,
    }),
    formattedTime: dateFormatter.format(new Date(transferRequest.createdAt)),
    transfersRoute: { name: "TransferRequests", params: { accountId: transferRequest.accountId } },
  }));
});

const loadDashboard = async (options?: { silent?: boolean }) => {
  if (!user.supervisorOrgId) return;

  if (!options?.silent) {
    loading.value = true;
  }

  try {
    dashboardData.value = await call("getSupervisorOrgDashboard")({
      supervisorOrgId: user.supervisorOrgId,
    });
  } catch (cause) {
    const error = new Error(`Error fetching supervisor org dashboard for ${user.supervisorOrgId}`, {
      cause,
    });
    console.error(error);
    captureException(error);
    showErrorToast();
  } finally {
    if (!options?.silent) {
      loading.value = false;
    }
  }
};

onMounted(async () => {
  if (!user.supervisorOrgId) {
    loading.value = false;
    return;
  }
  await loadDashboard();
});

const openPendingTransferRequest = (accountId: string, transferRequestId: string) => {
  drawerAccountId.value = accountId;
  openTransferRequestDrawer(transferRequestId);
};

watch(selectedTransferRequestId, async (transferRequestId) => {
  if (!transferRequestId) {
    drawerAccountId.value = null;
    await loadDashboard({ silent: true });
  }
});

const proAccountBalance = computed(() => dashboardData.value?.proAccount.balance ?? 0);
const openBankingBalance = computed(() => dashboardData.value?.openBanking.balance ?? 0);
const globalBalance = computed(() => proAccountBalance.value + openBankingBalance.value);

const totalConnectedAccounts = computed(() => {
  if (!dashboardData.value) return 0;
  return (
    dashboardData.value.proAccount.count.connected + dashboardData.value.openBanking.count.connected
  );
});

const primaryCards = computed(() => {
  if (!dashboardData.value) return [];
  return [
    {
      key: "global",
      label: t("supervisorOrgDashboard.metrics.globalOutstanding"),
      value: formatEuro(globalBalance.value),
      subtitle: t("supervisorOrgDashboard.metrics.accountsCount", {
        count: totalConnectedAccounts.value,
      }),
    },
    {
      key: "proAccount",
      label: t("supervisorOrgDashboard.metrics.proAccountAccounts"),
      value: formatEuro(proAccountBalance.value),
      subtitle: t("supervisorOrgDashboard.metrics.accountsCount", {
        count: dashboardData.value.proAccount.count.connected,
      }),
    },
    {
      key: "openBanking",
      label: t("supervisorOrgDashboard.metrics.openBankingAccounts"),
      value: formatEuro(openBankingBalance.value),
      subtitle: t("supervisorOrgDashboard.metrics.accountsCount", {
        count: dashboardData.value.openBanking.count.connected,
      }),
    },
  ];
});

const secondaryMetrics = computed(() => {
  if (!dashboardData.value) return [];
  return [
    {
      key: "proAccount",
      label: t("supervisorOrgDashboard.metrics.proAccountAccounts"),
      value: dashboardData.value.proAccount.count.connected,
    },
    {
      key: "openBankingConnected",
      label: t("supervisorOrgDashboard.metrics.openBankingShort"),
      value: dashboardData.value.openBanking.count.connected,
    },
    {
      key: "transferRequests",
      label: t("supervisorOrgDashboard.metrics.transfersToApprove"),
      value: dashboardData.value.pendingTransferRequests.length,
      valueClass: "text-primary",
    },
    {
      key: "proAccountPending",
      label: t("supervisorOrgDashboard.metrics.accountsOpening"),
      value: dashboardData.value.proAccount.count.pending,
      valueClass: "text-warning",
    },
    {
      key: "openBankingPending",
      label: t("supervisorOrgDashboard.metrics.openBankingConnectionsPending"),
      value: dashboardData.value.openBanking.count.pending,
      valueClass: "text-warning",
    },
  ];
});
</script>

<style scoped>
.supervisor-org-dashboard__label {
  font-size: 0.6875rem;
  font-weight: 500;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: rgba(var(--v-theme-on-surface), 0.55);
}

.supervisor-org-dashboard__value {
  font-size: 2rem;
  font-weight: 700;
  line-height: 1.2;
  color: rgb(var(--v-theme-on-surface));
}

.supervisor-org-dashboard__secondary-value {
  font-size: 1.75rem;
  font-weight: 700;
  line-height: 1.2;
}

.supervisor-org-dashboard__subtitle {
  font-size: 0.875rem;
  color: rgba(var(--v-theme-on-surface), 0.55);
}

.supervisor-org-dashboard__secondary-col {
  position: relative;
  display: flex;
  align-items: stretch;
}

.supervisor-org-dashboard__metric-cell {
  width: 100%;
  height: 100%;
}

.supervisor-org-dashboard__label,
.supervisor-org-dashboard__subtitle {
  line-height: 1.35;
}

.supervisor-org-dashboard__divider {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  height: 100%;
}

.supervisor-org-dashboard__account-name {
  line-height: 1.3;
}

.supervisor-org-dashboard__account-link {
  color: rgb(var(--v-theme-primary));
  text-decoration: none;
}

.supervisor-org-dashboard__account-link:hover {
  text-decoration: underline;
}

.supervisor-org-dashboard__approval-amount {
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1.2;
  color: rgb(var(--v-theme-on-surface));
}

.supervisor-org-dashboard__approval-card {
  height: 100%;
}
</style>
